/**
 * `/rep-firms/:id` — a distributor or rep firm: their pipeline, their design
 * wins, their contacts, and the number that converts what the end customer
 * pays into what Menlo books.
 *
 * **The counts here are read off the opportunities' current status, not off
 * closure history.** That is a different question from Phase 5's win rate,
 * which reads `v_closure` and excludes channel-imported closures from win
 * *reason* analysis — so the two figures can legitimately differ and this page
 * says which one it is showing rather than implying they are the same number.
 */

import { useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useApiQuery, type Paged, type Row } from '@/lib/queries';
import { useReferenceData } from '@/lib/reference';
import { Badge } from '@/components/ui/badge';
import { ContactsPanel } from '@/features/pickers/ContactsPanel';
import { MoneyCell } from '@/components/domain/Money';
import { formatMoney, humanize, sumMoney } from '@/lib/format';

export function RepFirmDetail(): React.ReactElement {
  const { id = '' } = useParams();
  const reference = useReferenceData();
  const firm = reference.rep_firms.find((candidate) => candidate.id === id);

  const opportunities = useApiQuery<Paged<Row>>(
    ['opportunities', 'rep-firm', id],
    `/api/opportunities?repFirmId=${id}&pageSize=200`,
  );
  const items = opportunities.data?.items ?? [];

  const figures = useMemo(() => {
    const open = items.filter((row) => row['status'] === 'open');
    const won = items.filter((row) => row['status'] === 'won');
    const decided = items.filter((row) =>
      ['won', 'lost', 'no_decision', 'design_win_lapsed'].includes(String(row['status'])),
    );
    return {
      openNet: sumMoney(open.map((row) => row['annual_revenue_net'] as string)),
      openCustomer: sumMoney(open.map((row) => row['annual_revenue_customer'] as string)),
      openCount: open.length,
      wonCount: won.length,
      decidedCount: decided.length,
      winRate: decided.length === 0 ? null : Math.round((won.length / decided.length) * 100),
    };
  }, [items]);

  if (firm === undefined) {
    return <p className="text-sm text-muted-foreground">Loading…</p>;
  }

  return (
    <div className="space-y-4">
      <header className="rounded-lg border bg-card p-4">
        <h1 className="text-lg font-semibold">{firm.name}</h1>
        <p className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
          <Badge variant="outline">{humanize(firm.firm_type)}</Badge>
          <Badge variant="outline">{firm.region}</Badge>
          {!firm.is_active && <Badge variant="risk">Inactive</Badge>}
        </p>

        <div className="mt-3 rounded-md bg-muted/50 p-3 text-xs">
          <p className="font-medium">
            Net factor {firm.net_factor} — a {firm.markup_pct}% markup
          </p>
          <p className="mt-1 text-muted-foreground">
            {firm.firm_type === 'distributor' ? (
              <>
                The distributor buys at Menlo&rsquo;s price and marks it up by {firm.markup_pct}%,
                so Menlo books {(Number(firm.net_factor) * 100).toFixed(1)}% of the resale price.
                Read as a discount off resale instead of a markup on cost, the same percentage gives
                a different — and wrong — number.
              </>
            ) : (
              <>
                A rep never takes title: Menlo invoices the customer at the resale price and pays
                commission separately, so the factor is 1 and no conversion happens. Netting the
                commission here would understate revenue and double-count the cost.
              </>
            )}
          </p>
        </div>
      </header>

      <section className="grid gap-3 sm:grid-cols-4">
        <Metric
          label="Open pipeline (net)"
          value={formatMoney(figures.openNet)}
          hint={`${figures.openCount} open`}
        />
        <Metric
          label="Open pipeline (customer)"
          value={formatMoney(figures.openCustomer)}
          hint="What the end customers pay"
        />
        <Metric label="Design wins" value={String(figures.wonCount)} hint="Currently won" />
        <Metric
          label="Win rate"
          value={figures.winRate === null ? '—' : `${figures.winRate}%`}
          hint={`${figures.wonCount} of ${figures.decidedCount} decided`}
        />
      </section>

      <section className="rounded-lg border bg-card p-4">
        <h2 className="mb-2 text-sm font-medium">Opportunities</h2>
        <ul className="divide-y">
          {items.map((row) => (
            <li
              key={String(row['id'])}
              className="flex items-center justify-between gap-2 py-1.5 text-sm"
            >
              <span className="min-w-0">
                <Link
                  to={`/opportunities/${String(row['id'])}`}
                  className="block truncate font-medium hover:underline"
                >
                  {String(row['name'])}
                </Link>
                <span className="text-xs text-muted-foreground">
                  {String(row['account_name'])} · {String(row['stage_name'])}
                </span>
              </span>
              <MoneyCell value={row['annual_revenue_net'] as string} />
            </li>
          ))}
          {items.length === 0 && (
            <li className="py-3 text-xs text-muted-foreground">Nothing through this firm yet.</li>
          )}
        </ul>
      </section>

      <section className="rounded-lg border bg-card p-4">
        <ContactsPanel repFirmId={id} canWrite />
      </section>
    </div>
  );
}

function Metric({
  label,
  value,
  hint,
}: {
  label: string;
  value: string;
  hint: string;
}): React.ReactElement {
  return (
    <div className="rounded-lg border bg-card p-3">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="text-lg font-semibold tabular-nums">{value}</p>
      <p className="text-[11px] text-muted-foreground">{hint}</p>
    </div>
  );
}
